import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, ChevronDown, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { useSession } from "../../auth";
import { actorName, personLabel } from "../../lib/people";
import { cn } from "../../lib/utils";
import { useFactory } from "../../store";
import type { IssueActivity, IssueComment, IssueDetail, UserBadgeInfo } from "../../types";
import { TimeAgo } from "../time-ago";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { Tabs, TabsContent } from "../ui/tabs";
import { ViewTab, ViewTabsList } from "../view-tabs";
import { Markdown } from "./markdown";
import { MentionField } from "./mention-field";
import { PRIORITY_LABELS, useIssuesMeta } from "./meta";
import { ShowMore } from "./show-more";

/** Feed entries shown before "Show older". */
const FEED_PREVIEW = 8;

export function ActivityBlock({ issue }: { issue: IssueDetail }) {
  const queryClient = useQueryClient();
  const comments = useQuery({
    queryKey: ["issue-comments", issue.identifier],
    queryFn: () => api.issueComments(issue.identifier),
    refetchInterval: 10_000,
  });
  const activity = useQuery({
    queryKey: ["issue-activity", issue.identifier],
    queryFn: () => api.issueActivity(issue.identifier),
    refetchInterval: 20_000,
  });
  const session = useSession();
  const viewer = { displayName: session.name, email: session.email };
  const users = useIssuesMeta().data?.users ?? [];
  const [draft, setDraft] = useState("");
  const [tab, setTab] = useState<"comments" | "activity">("comments");
  const [showAll, setShowAll] = useState(false);
  const post = useMutation({
    mutationFn: () => api.addComment(issue.identifier, { body: draft.trim() }),
    onSuccess: () => {
      setDraft("");
      void queryClient.invalidateQueries({ queryKey: ["issue-comments", issue.identifier] });
      void queryClient.invalidateQueries({ queryKey: ["inbox"] });
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Comment failed"),
  });

  // Newest first. Comments and the audit trail each get a tab, so a long trail does not bury the conversation.
  const allComments = [...(comments.data?.comments ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  const trail = (activity.data?.activity ?? [])
    .filter((entry) => entry.kind !== "comment")
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const list = tab === "comments" ? allComments : trail;
  const visibleCount = showAll ? list.length : FEED_PREVIEW;
  const older = Math.max(list.length - visibleCount, 0);

  const submit = () => {
    if (draft.trim() && !post.isPending) post.mutate();
  };

  return (
    <section aria-label="Discussion" className="border-t pt-5">
      <Tabs
        value={tab}
        onValueChange={(next) => {
          setTab(next as "comments" | "activity");
          setShowAll(false);
        }}
      >
        <ViewTabsList className="gap-4 border-b">
          <FeedTab value="comments" label="Comments" count={allComments.length} />
          <FeedTab value="activity" label="Activity" count={trail.length} />
        </ViewTabsList>
        <TabsContent value="comments" className="pt-3">
          <form
            className="mb-4 flex gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <Avatar className="mt-1.5 size-6">
              <AvatarFallback className="text-[10px]">{initials(viewer)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 rounded-lg border border-input bg-card shadow-xs transition-[border-color,box-shadow] duration-(--motion-dur) focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30">
              <MentionField
                value={draft}
                onChange={setDraft}
                onSubmit={submit}
                users={users}
                disabled={post.isPending}
              />
              <div className="flex items-center justify-between gap-2 px-3 pb-2">
                <span className="text-xs text-muted-foreground">
                  Markdown supported
                  <span className="hidden pointer-fine:inline">
                    {" "}
                    · <kbd className="font-sans">⌘</kbd>
                    <kbd className="font-sans">↵</kbd> to post
                  </span>
                </span>
                <Button type="submit" size="sm" disabled={!draft.trim() || post.isPending}>
                  <Send className="size-3.5" />
                  {post.isPending ? "Posting…" : "Comment"}
                </Button>
              </div>
            </div>
          </form>
          {allComments.length === 0 ? (
            <p className="py-2 text-xs text-muted-foreground">No comments yet. Start the conversation.</p>
          ) : (
            <ul className="divide-y divide-border/70">
              {allComments.slice(0, visibleCount).map((comment) => (
                <CommentItem key={comment.id} comment={comment} users={users} />
              ))}
            </ul>
          )}
        </TabsContent>
        <TabsContent value="activity" className="pt-3">
          {trail.length === 0 ? (
            <p className="py-2 text-xs text-muted-foreground">Nothing recorded yet.</p>
          ) : (
            <ul>
              {trail.slice(0, visibleCount).map((entry) => (
                <li key={entry.id} className="flex items-start gap-2.5 py-1.5 text-xs text-muted-foreground">
                  <span className="flex size-5 shrink-0 items-center justify-center">
                    <span className="size-1.5 rounded-full bg-border" />
                  </span>
                  <span className="min-w-0 flex-1 leading-5">
                    <ActivityLine activity={entry} />
                  </span>
                  <TimeAgo at={entry.createdAt} className="shrink-0 leading-5 tabular-nums" />
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
      {older > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 w-full text-muted-foreground"
          onClick={() => setShowAll(true)}
        >
          <ChevronDown className="size-3.5" />
          Show {older} older {older === 1 ? "entry" : "entries"}
        </Button>
      )}
    </section>
  );
}

function FeedTab({ value, label, count }: { value: string; label: string; count: number }) {
  return (
    <ViewTab value={value} className="h-8">
      {label}
      <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
    </ViewTab>
  );
}

function initials(user: { displayName: string; email: string } | null | undefined): string {
  return personLabel(user, "?")
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** The Verifier posts its developer review as a separate comment headed `## Intent`. */
function isIntentComment(comment: IssueComment): boolean {
  return comment.agentId === "verifier" && /^##\s+Intent\s*(\n|$)/.test(comment.body.trimStart());
}

/**
 * One comment: avatar, who and when on a line, then the body. Comments are unboxed and
 * separated by hairlines; only the Verifier's Intent review keeps its gradient frame.
 */
export function CommentItem({
  comment,
  users,
  className,
}: {
  comment: IssueComment;
  users: UserBadgeInfo[];
  className?: string;
}) {
  const author = comment.author;
  const intent = isIntentComment(comment);
  return (
    <li id={`comment-${comment.id}`} className={cn("flex gap-3 py-3 scroll-mt-20 first:pt-1", className)}>
      <Avatar className="mt-0.5 size-6">
        <AvatarFallback className="text-[10px]">
          {comment.agentId ? <Bot className="size-3.5" /> : initials(author)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
          <span className="text-sm font-medium">{personLabel(author, "Unknown")}</span>
          {author && author.kind !== "system" && author.email.includes("@") && (
            <span className="hidden min-w-0 truncate text-muted-foreground sm:inline">{author.email}</span>
          )}
          {comment.agentId && (
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {comment.agentId} station
            </span>
          )}
          <TimeAgo at={comment.createdAt} className="text-muted-foreground tabular-nums" />
        </div>
        <div className={intent ? "rainbow-border rounded-lg px-3 py-2" : undefined}>
          <ShowMore>
            <Markdown className="text-sm" mentions={users}>
              {comment.body}
            </Markdown>
          </ShowMore>
        </div>
      </div>
    </li>
  );
}

function ActivityActor({ activity }: { activity: IssueActivity }) {
  if (activity.agentId) return <>Skynet · {activity.agentId}</>;
  const actor = activity.actor;
  if (!actor || actor.kind === "system") return <>{personLabel(actor, "Someone")}</>;
  return (
    <>
      <span className="font-medium text-foreground/80">{personLabel(actor)}</span>
      {actor.email.includes("@") && <span className="hidden sm:inline"> · {actor.email}</span>}
    </>
  );
}

function namedPerson(value: unknown): string | null {
  return typeof value === "string" ? actorName(value) : null;
}

function activityText(input: unknown): string {
  if (input == null || input === "") return "none";
  if (typeof input === "string" || typeof input === "number" || typeof input === "boolean")
    return String(input);
  return "updated";
}

function fieldChange(activity: IssueActivity): string {
  const next = activity.toValue;
  switch (activity.field) {
    case "description":
      return "updated the description";
    case "testingInstructions":
      return "updated the testing instructions";
    case "testSteps": {
      const count = Number(next);
      if (!count) return "removed the test steps";
      const steps = `${count} test ${count === 1 ? "step" : "steps"}`;
      return Number(activity.fromValue) ? `replaced the test steps with ${steps}` : `added ${steps}`;
    }
    case "title":
      return `renamed this to “${activityText(next)}”`;
    case "priority": {
      const label = PRIORITY_LABELS[Number(next)];
      return label ? `set priority to ${label}` : "changed the priority";
    }
    case "reviewer": {
      const name = namedPerson(next);
      return next == null
        ? "cleared the reviewer"
        : name
          ? `set the reviewer to ${name}`
          : "changed the reviewer";
    }
    case "reporter": {
      const name = namedPerson(next);
      return name ? `set the reporter to ${name}` : "changed the reporter";
    }
    case "storyPoints":
      return next == null ? "cleared the estimate" : `set the estimate to ${activityText(next)}`;
    case "estimate": {
      const points =
        next && typeof next === "object" && "storyPoints" in next ? activityText(next.storyPoints) : null;
      return points ? `set the estimate to ${points}` : "updated the estimate";
    }
    case "prUrl":
      return next ? "linked a pull request" : "removed the pull request";
    case "previewRequired":
      return next == null
        ? "cleared the preview decision"
        : next
          ? "marked this as needing a preview"
          : "marked this as not needing a preview";
    case "sla":
      return next ? `set the SLA to ${activityText(next)}` : "cleared the SLA";
    case "parent":
      return next ? `set the parent to ${activityText(next)}` : "cleared the parent";
    case "type":
      return `changed the type to ${activityText(next)}`;
    default:
      return `updated ${activity.field ?? "the issue"}`;
  }
}

const PREVIEW_ACTIVITY_TEXT: Record<string, string> = {
  "preview.requested": "requested a preview environment",
  "preview.provisioning": "started building the preview environment",
  "preview.updating": "is rebuilding the preview at the new head",
  "preview.ready": "brought the preview environment up",
  "preview.failed": "could not build the preview environment",
  "preview.stopped": "stopped the preview environment",
  "preview.expired": "let the preview environment expire",
};

function delegatedStation(toValue: unknown, names: Map<string, { name: string }>): string | null {
  if (!toValue || typeof toValue !== "object") return null;
  const id = (toValue as { startAgentId?: unknown }).startAgentId;
  if (typeof id !== "string" || !id) return null;
  return names.get(id)?.name ?? id;
}

function ActivityLine({ activity }: { activity: IssueActivity }) {
  const { agentById } = useFactory();
  const actor = <ActivityActor activity={activity} />;
  if (activity.kind.startsWith("preview.")) {
    return (
      <span>
        {actor} {PREVIEW_ACTIVITY_TEXT[activity.kind] ?? "updated the preview environment"}
      </span>
    );
  }
  switch (activity.kind) {
    case "created":
      return <span>{actor} created the issue</span>;
    case "imported": {
      const key =
        activity.toValue && typeof activity.toValue === "object" && "jiraKey" in activity.toValue
          ? activityText((activity.toValue as { jiraKey: unknown }).jiraKey)
          : null;
      return <span>Skynet imported the issue from Jira{key ? ` (${key})` : ""}</span>;
    }
    case "status":
      return (
        <span>
          {actor} moved {activityText(activity.fromValue)} → {activityText(activity.toValue)}
        </span>
      );
    case "comment":
      return <span>{actor} commented</span>;
    case "signoff":
      return activity.toValue === "approved" ? (
        <span>{actor} signed off the issue for merge</span>
      ) : activity.toValue === "cancelled" ? (
        <span>{actor} cancelled the issue in business testing and closed its pull request</span>
      ) : (
        <span>{actor} requested changes in business testing</span>
      );
    case "delegated": {
      const station = delegatedStation(activity.toValue, agentById);
      return (
        <span>
          {actor} sent the issue to Skynet{station ? `, starting at the ${station}` : ""}
        </span>
      );
    }
    case "archived":
      return (
        <span>
          {actor} {activity.toValue ? "archived" : "restored"} the issue
        </span>
      );
    case "release":
      if (activity.toValue && activity.fromValue) {
        return (
          <span>
            {actor} moved the issue from {activityText(activity.fromValue)} to{" "}
            {activityText(activity.toValue)}
          </span>
        );
      }
      if (activity.toValue) {
        return (
          <span>
            {actor} added the issue to {activityText(activity.toValue)}
          </span>
        );
      }
      return (
        <span>
          {actor} removed the issue from {activityText(activity.fromValue)}
        </span>
      );
    case "sentry":
      return activity.toValue &&
        typeof activity.toValue === "object" &&
        (activity.toValue as { status?: unknown }).status === "resolved" ? (
        <span>{actor} resolved the Sentry issue</span>
      ) : (
        <span>{actor} could not resolve the Sentry issue</span>
      );
    case "relation":
      return (
        <span>
          {actor} linked {activity.field}: {activityText(activity.toValue ?? activity.fromValue)}
        </span>
      );
    case "label":
    case "teams":
      return (
        <span>
          {actor} changed {activity.field}
        </span>
      );
    default:
      return (
        <span>
          {actor} {fieldChange(activity)}
        </span>
      );
  }
}
